// TMOS Template Service
// Date: January 26, 2025
// Purpose: Frontend service for template management using existing backend TemplateService

import api from './api';
import { PureHierarchicalTemplate } from '../types/pureHierarchicalTemplate_v2';

export interface Template {
  id: string;
  tenant_id: string;
  category_id?: string;
  name: string;
  description?: string;
  template_type: string;
  content: any;
  marketplace_visibility: 'DRAFT' | 'PRIVATE' | 'PUBLIC';
  // Deprecated: Use marketplace_visibility instead
  is_public?: boolean;
  is_published?: boolean;
  version: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  owner_name?: string | null;
  owner_email?: string | null;
  // Additional properties for compatibility
  is_ready_made?: boolean;
  sample_data?: Record<string, any>;
  metadata?: Record<string, any>;
  usage_count?: number;
  last_used_at?: string;
  sub_type?: string;
  industry_type?: string;
}

export interface CreateTemplateRequest {
  tenant_id: string;
  category_id?: string;
  name: string;
  description?: string;
  template_type: string;
  content: any;
  marketplace_visibility?: 'DRAFT' | 'PRIVATE' | 'PUBLIC';
  // Deprecated: Use marketplace_visibility instead
  is_public?: boolean;
  is_published?: boolean;
  version?: string;
  created_by: string;
}

export interface TemplateFilters {
  template_type?: string;
  marketplace_visibility?: 'DRAFT' | 'PRIVATE' | 'PUBLIC';
  // Deprecated: Use marketplace_visibility instead
  is_public?: boolean;
  is_published?: boolean;
  search?: string;
  created_by?: string;
}

class TemplateService {
  private baseUrl = '/templates';

  /**
   * Create a new template using the existing backend TemplateService
   */
  async createTemplate(templateData: CreateTemplateRequest): Promise<Template> {
    try {
      const response = await api.post(this.baseUrl, templateData);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to create template');
      }
    } catch (error) {
      console.error('Error creating template:', error);
      throw error;
    }
  }

  /**
   * Create a pure hierarchical template
   */
  async createHierarchicalTemplate(
    template: PureHierarchicalTemplate,
    tenantId: string,
    userId: string
  ): Promise<Template> {
    const templateData: CreateTemplateRequest = {
      tenant_id: tenantId,
      name: template.template_metadata.name,
      description: template.template_metadata.description,
      template_type: 'ROADMAP',
      content: template,
      marketplace_visibility: 'DRAFT', // Default to DRAFT, user can publish later
      version: template.template_metadata.version,
      created_by: userId
    };

    // Use the hierarchical endpoint that bypasses validation
    try {
      const response = await api.post(`${this.baseUrl}/hierarchical`, templateData);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to create hierarchical template');
      }
    } catch (error) {
      console.error('Error creating hierarchical template:', error);
      throw error;
    }
  }

  /**
   * Get template by ID
   */
  async getTemplate(templateId: string): Promise<Template> {
    try {
      const response = await api.get(`${this.baseUrl}/${templateId}`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Template not found');
      }
    } catch (error) {
      console.error('Error loading template:', error);
      throw error;
    }
  }

  /**
   * Get all templates with optional filters
   */
  async getTemplates(filters: TemplateFilters = {}): Promise<Template[]> {
    try {
      const params = new URLSearchParams();
      
      if (filters.template_type) params.append('template_type', filters.template_type);
      if (filters.marketplace_visibility) params.append('marketplace_visibility', filters.marketplace_visibility);
      // Legacy support
      if (filters.is_public !== undefined) params.append('is_public', filters.is_public.toString());
      if (filters.is_published !== undefined) params.append('is_published', filters.is_published.toString());
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`${this.baseUrl}?${params.toString()}`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to load templates');
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      throw error;
    }
  }

  /**
   * Update an existing template
   */
  async updateTemplate(templateId: string, templateData: Partial<CreateTemplateRequest>): Promise<Template> {
    try {
      const response = await api.put(`${this.baseUrl}/${templateId}`, templateData);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to update template');
      }
    } catch (error) {
      console.error('Error updating template:', error);
      throw error;
    }
  }

  /**
   * Delete a template
   */
  async deleteTemplate(templateId: string): Promise<void> {
    try {
      const response = await api.delete(`${this.baseUrl}/${templateId}`);
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      throw error;
    }
  }

  // =====================================================
  // TEMPLATE VISIBILITY MANAGEMENT
  // =====================================================

  /**
   * Update template visibility (DRAFT, PRIVATE, PUBLIC)
   */
  async updateVisibility(templateId: string, visibility: 'DRAFT' | 'PRIVATE' | 'PUBLIC'): Promise<Template> {
    try {
      const response = await api.put(`${this.baseUrl}/${templateId}`, {
        marketplace_visibility: visibility
      });
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to update template visibility');
      }
    } catch (error) {
      console.error('Error updating template visibility:', error);
      throw error;
    }
  }

  /**
   * Publish template to marketplace (PUBLIC or PRIVATE)
   */
  async publishTemplateToMarketplace(templateId: string, visibility: 'PUBLIC' | 'PRIVATE' = 'PUBLIC'): Promise<void> {
    try {
      const response = await api.post(`/marketplace/roadmaps/templates/${templateId}/publish-to-marketplace`, {
        visibility
      });
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to publish template to marketplace');
      }
    } catch (error) {
      console.error('Error publishing template to marketplace:', error);
      throw error;
    }
  }

  // =====================================================
  // TEMPLATE ACCESS CONTROL
  // =====================================================

  /**
   * Request access to a private template
   */
  async requestAccess(templateId: string, requestMessage?: string): Promise<any> {
    try {
      const response = await api.post(`/marketplace/roadmaps/templates/${templateId}/request-access`, {
        request_message: requestMessage
      });
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to request access');
      }
    } catch (error) {
      console.error('Error requesting access:', error);
      throw error;
    }
  }

  /**
   * Check if user has access to a template
   */
  async checkAccess(templateId: string): Promise<boolean> {
    try {
      const response = await api.get(`/marketplace/roadmaps/templates/${templateId}/check-access`);
      
      if (response.data.success) {
        return response.data.data.hasAccess === true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Error checking access:', error);
      return false;
    }
  }

  /**
   * Get pending access requests for templates owned by current user
   */
  async getPendingAccessRequests(): Promise<any[]> {
    try {
      const response = await api.get('/marketplace/roadmaps/templates/access-requests/pending');
      
      if (response.data.success) {
        return response.data.data || [];
      } else {
        throw new Error(response.data.error || 'Failed to get pending requests');
      }
    } catch (error) {
      console.error('Error getting pending requests:', error);
      throw error;
    }
  }

  /**
   * Get access requests made by current user
   */
  async getMyAccessRequests(): Promise<any[]> {
    try {
      const response = await api.get('/marketplace/roadmaps/templates/access-requests/my-requests');
      
      if (response.data.success) {
        return response.data.data || [];
      } else {
        throw new Error(response.data.error || 'Failed to get my requests');
      }
    } catch (error) {
      console.error('Error getting my requests:', error);
      throw error;
    }
  }

  /**
   * Get last access request for a specific template
   */
  async getLastAccessRequest(templateId: string): Promise<any | null> {
    try {
      const requests = await this.getMyAccessRequests();
      // Filter requests for this template and get the most recent one
      const templateRequests = requests
        .filter((req: any) => req.template_id === templateId)
        .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      
      return templateRequests.length > 0 ? templateRequests[0] : null;
    } catch (error) {
      console.error('Error getting last access request:', error);
      return null;
    }
  }

  /**
   * Approve an access request
   */
  async approveAccessRequest(requestId: string, reviewNotes?: string): Promise<any> {
    try {
      const response = await api.post(`/marketplace/roadmaps/templates/access-requests/${requestId}/approve`, {
        reviewNotes
      });
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to approve request');
      }
    } catch (error) {
      console.error('Error approving request:', error);
      throw error;
    }
  }

  /**
   * Reject an access request
   */
  async rejectAccessRequest(requestId: string, reviewNotes?: string): Promise<void> {
    try {
      const response = await api.post(`/marketplace/roadmaps/templates/access-requests/${requestId}/reject`, {
        reviewNotes
      });
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to reject request');
      }
    } catch (error) {
      console.error('Error rejecting request:', error);
      throw error;
    }
  }

  /**
   * Grant access to a template (proactive grant)
   */
  async grantAccess(
    templateId: string,
    tenantId: string,
    userId: string,
    accessType: 'READ' | 'WRITE' | 'ADMIN' = 'READ',
    expiresAt?: Date
  ): Promise<any> {
    try {
      const response = await api.post(`/marketplace/roadmaps/templates/${templateId}/grant-access`, {
        tenantId,
        userId,
        accessType,
        expiresAt: expiresAt ? expiresAt.toISOString() : undefined
      });
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to grant access');
      }
    } catch (error) {
      console.error('Error granting access:', error);
      throw error;
    }
  }

  /**
   * Revoke access to a template
   */
  async revokeAccess(templateId: string, grantId: string): Promise<void> {
    try {
      const response = await api.delete(`/marketplace/roadmaps/templates/${templateId}/access-grants/${grantId}`);
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to revoke access');
      }
    } catch (error) {
      console.error('Error revoking access:', error);
      throw error;
    }
  }

  /**
   * List all access grants for a template (owner only)
   */
  async listAccessGrants(templateId: string): Promise<any[]> {
    try {
      const response = await api.get(`/marketplace/roadmaps/templates/${templateId}/access-grants`);
      
      if (response.data.success) {
        return response.data.data || [];
      } else {
        throw new Error(response.data.error || 'Failed to list access grants');
      }
    } catch (error) {
      console.error('Error listing access grants:', error);
      throw error;
    }
  }

  /**
   * Search tenants for access granting
   */
  async searchTenants(query: string): Promise<any[]> {
    try {
      const response = await api.get(`/marketplace/roadmaps/search/tenants?search=${encodeURIComponent(query)}`);
      
      if (response.data.success) {
        return response.data.data || [];
      } else {
        throw new Error(response.data.error || 'Failed to search tenants');
      }
    } catch (error) {
      console.error('Error searching tenants:', error);
      throw error;
    }
  }

  /**
   * Search users for access granting
   */
  async searchUsers(query: string, tenantId?: string): Promise<any[]> {
    try {
      const params = new URLSearchParams();
      params.append('search', query);
      if (tenantId) params.append('tenantId', tenantId);
      
      const response = await api.get(`/marketplace/roadmaps/search/users?${params.toString()}`);
      
      if (response.data.success) {
        return response.data.data || [];
      } else {
        throw new Error(response.data.error || 'Failed to search users');
      }
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  }
}

export const templateService = new TemplateService();
export default templateService;
